<template>
  <SlideHintsInput
    :isSortingHints="isSortingHints"
    :hintLabel="$i18nPresenter.t('Hints')"
    :hintTooltip="$i18nPresenter.t('Type your hints here')"
    :hintPlaceholder="$i18nPresenter.t('Hint')"
    :keyInput="key"
    @sortHints="sortHints"
    @updateHintTitle="updateHintTitle"
    @addHint="addHint"
    @deleteHint="deleteHint"
    v-if="isSlideHintsInputVisible"
    class="animate__animated animate__fadeIn animate__faster"
  />
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import Utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import Socket from '@/socket'
import EventBus from '@/eventBus'
import HintApi from '@/api/hint'
import SlideHintsInput from '@/components-v2/presentation-editor/side-right/hints/SlideHintsInput.vue'
import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'
import UpdateTitleSlideMixin from '@/mixins/undo-redo/UpdateTitleSlideMixin'
import SlideApi from '@/api/slide'

export default {
  components: {
    SlideHintsInput,
  },
  mixins: [UndoRedoMixin, UpdateTitleSlideMixin],
  data() {
    return {
      isSortingHints: false,
      key: null,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
    hasNoInput() {
      return this.slideActive && (!this.slideActive.hints || !this.slideActive.hints.length)
    },
    hasHints() {
      return this.slideActive && this.slideActive.hints && this.slideActive.hints.length
      && this.slideActive.hints.some((hint) => hint.title && hint.title.length)
    },
    isPickAnswerOrPickImage() {
      const { type, slideType } = this.slideActive
      return type === 'pickAnswer' && (!slideType || slideType === 'imageChoice')
    },
    isSlideHintsInputVisible() {
      return this.slideActive.isHintsVisible && !this.presentation.audiencePacing
        && this.isPickAnswerOrPickImage
    },
  },
  watch: {
    'slideActive.isHintsVisible': function watchIsHintsVisible() {
      this.showDefaultHintOptions()
    },
  },
  mounted() {
    this.showDefaultHintOptions()
  },
  methods: {
    ...mapActions([
      'updateSlideActiveAction',
      'setSlideActiveAction',
      'setSyncLoadingAction',
      'createHintAction',
    ]),
    ...mapGetters(['getSlideActive']),

    _handleSortHints() {
      if (!this.hasHints) return
      this.slideActive.hints.sort((a, b) => {
        return a.order - b.order
      })
    },

    async sortHints(event) {
      const { hints } = this.slideActive

      if (!hints || hints.length < 2 || !event || !event.moved) return

      const { newIndex, oldIndex, element: movedHint } = event.moved

      if (newIndex === oldIndex) return
      if (this.isSortingHints) return

      this.isSortingHints = true
      let newOrder = null
      if (newIndex === hints.length - 1) {
        newOrder = hints[hints.length - 2].order + 1
      } else if (newIndex === 0) {
        newOrder = hints[1].order / 2
      } else {
        newOrder = (hints[newIndex + 1].order + hints[newIndex - 1].order) / 2
      }

      const newHints = hints.map((option) => {
        if (option.id === movedHint.id) {
          return { ...option, order: newOrder }
        }

        return option
      })

      this.updateSlideActiveAction({
        hints: newHints,
      })

      if (!movedHint.id) {
        this.isSortingHints = false
      }

      if (movedHint.id) {
        this.setSyncLoadingAction(1)
        try {
          await HintApi.patchHintById(movedHint.id, {
            order: newOrder,
            presentationId: this.presentation.id,
            uniqueAccessCode: this.presentation.uniqueAccessCode, // for real-time collab
            socketId: Socket.getSocketId(),
          })

          this.setSyncLoadingAction(2)
          this.isSortingHints = false
        } catch (error) {
          this.setSyncLoadingAction(3)
          this.isSortingHints = false
          throw error
        }
      }
    },

    async updateHintTitle(data) {
      this.setSyncLoadingAction(1)
      let currentHint = null
      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Enter hint',
        data.value,
        '',
        {
          slideType: Utils.getCorrectSlideTypeName(this.slideActive.type,
            this.slideActive.slideType),
          slideId: this.slideActive.id,
          presentationId: this.presentation.id,
        },
      )
      if (data.id && this.slideActive.hints) {
        currentHint = this.getCurrentHint(data)
      } else {
        this._handleCreateHint(data)
      }
      const canUpdateTitle = currentHint && data.value !== currentHint.title
      if (canUpdateTitle) {
        currentHint.title = data.value
        this._handleUpdateHintTitle(data)
      }
    },

    async _handleCreateHint(data) {
      try {
        const currentHints = this.slideActive.hints
        const highestOrder = this.slideActive.hints.reduce((accumulator, item) => {
          return item.order > accumulator ? item.order : accumulator
        }, 1)
        const payload = {
          presentationId: this.presentation.id,
          socketId: Socket.getSocketId(),
          slideId: this.slideActive.id,
          title: data.value,
          order: this.slideActive.hints[data.index].order || highestOrder + 1,
          uniqueAccessCode: this.presentation.uniqueAccessCode,
        }
        currentHints[data.index].title = payload.title
        currentHints[data.index].order = payload.order
        this.updateSlideActiveAction({
          hints: currentHints,
        })
        const newHint = await HintApi.createHint(payload)
        currentHints[data.index].id = newHint.id
        this.updateSlideActiveAction({
          hints: currentHints,
        })
        EventBus.$emit('canvas-hints: jump-to-slide', data.index)
        this.setSyncLoadingAction(2)
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
        Utils.showToastError(this.$i18nPresenter.t('Create hint failed.'))
      }
    },

    async _handleUpdateHintTitle(data) {
      try {
        const currentHints = this.slideActive.hints
        const highestOrder = this.slideActive.hints.reduce((accumulator, item) => {
          return item.order > accumulator ? item.order : accumulator
        }, 1)
        const payload = {
          presentationId: this.presentation.id,
          socketId: Socket.getSocketId(),
          title: data.value,
          order: this.slideActive.hints[data.index].order || highestOrder + 1,
          uniqueAccessCode: this.presentation.uniqueAccessCode, // for real-time collab
        }
        currentHints[data.index].title = payload.title
        currentHints[data.index].order = payload.order
        this.updateSlideActiveAction({
          hints: currentHints,
        })
        await HintApi.patchHintById(data.id, payload)
        this.setSyncLoadingAction(2)
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
        Utils.showToastError(this.$i18nPresenter.t('Modify hint failed.'))
      }
    },

    getCurrentHint(eventData) {
      const { hints } = this.slideActive
      if (eventData.id && hints) {
        return hints.find((item) => item.id === eventData.id)
      }
      return hints[eventData.index]
    },

    async deleteHint(item, index) {
      try {
        const { hints } = this.slideActive

        this.$store.state.slideActive.hints.splice(index, 1)
        this.$store.state.slideActive.hints.push()
        const payload = {
          hints,
          isHintsVisible: !!hints.length,
        }
        this.updateSlideActiveAction(payload)

        if (!item.id) {
          if (this.slideActive.hints.length === 0) {
            SlideApi.patchSlideById(this.slideActive.id, {
              isHintsVisible: false,
              socketId: Socket.getSocketId(), // for real-time collab
              uniqueAccessCode: this.presentation.uniqueAccessCode,
            })
          }
          return
        }
        this.setSyncLoadingAction(1)
        await HintApi.deleteHint(item.id, {
          presentationId: this.presentation.id,
          slideId: this.slideActive.id,
          socketId: Socket.getSocketId(),
          uniqueAccessCode: this.presentation.uniqueAccessCode, // for real-time collab
        })

        this.setSyncLoadingAction(2)
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
        Utils.showToastError(this.$i18nPresenter.t('Delete hint failed.'))
      }
    },

    addHint(keyMode) {
      const { hints } = this.slideActive
      if (hints.length > 3) return
      hints.push({
        index: hints.length + 1,
        title: '',
        image: null,
        order: hints.length + 1,
      })
      this.updateSlideActiveAction({
        hints,
      })
      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Side right > Add hint',
        '',
        '',
        {
          slideType: Utils.getCorrectSlideTypeName(this.slideActive.type,
            this.slideActive.slideType),
          slideId: this.slideActive.id,
          presentationId: this.presentation.id,
        },
      )
      if (!keyMode) return
      setTimeout(() => {
        const inputId = `hintIndex${hints.length - 1}`
        if (!window.document.getElementById(inputId)) return
        window.document.getElementById(inputId).focus()
      }, 200)
    },

    async _createDefaultHint() {
      try {
        const params = {
          title: '',
          image: null,
          order: 1,
          slideId: this.slideActive.id,
          presentationId: this.presentation.id,
        }
        this.updateSlideActiveAction({
          hints: [params],
        })
      } catch (error) {
        Sentry.captureException(error)
      }
    },

    showDefaultHintOptions() {
      if (!this.slideActive.isHintsVisible) return
      if (this.hasNoInput) {
        this._createDefaultHint()
      }
    },
  },
}
</script>
